import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import './styles/login.css';
import { initiateTwoFactor, verifyTwoFactor, userLoginAndAuthentication } from '../redux/actions/userAction';
import { leaveForReturnTo } from '../Auth/returnTo';

function Verify2FA() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const emailFromState = location.state?.email;
    const email = emailFromState || localStorage.getItem('pending_2fa_email') || '';
    const purpose = location.state?.purpose || localStorage.getItem('pending_2fa_purpose') || 'login';
    const credentialsRaw = localStorage.getItem('pending_2fa_credentials');
    const credentials = credentialsRaw ? JSON.parse(credentialsRaw) : null;

    useEffect(() => {
        if (!email) {
            navigate('/login');
            return;
        }

        // Auto-send code on mount
        const sendCodeAutomatically = async () => {
            try {
                await dispatch(initiateTwoFactor(email, purpose));
                setCodeSent(true);
                setSuccess('Code sent to your email (expires in 1 minute)');
                setResendCooldown(20);
            } catch (err) {
                if (err.isRateLimit) {
                    // Code was already sent recently, just show input
                    setCodeSent(true);
                    setResendCooldown(20);
                    setSuccess('Code already sent to your email (expires in 1 minute)');
                } else {
                    setError(err?.response?.data?.message || err?.message || 'Failed to send code');
                }
            }
        };

        sendCodeAutomatically();
    }, [dispatch, email, purpose, navigate]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown((prev) => Math.max(prev - 1, 0));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const handleSendCode = async () => {
        if (resendCooldown > 0) return;
        
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            await dispatch(initiateTwoFactor(email, purpose));
            setSuccess('Code sent to your email (expires in 1 minute)');
            setCodeSent(true);
            setResendCooldown(20);
        } catch (err) {
            if (err.isRateLimit) {
                setError(err.message); // "Please wait 30 seconds before requesting another code"
            } else {
                setError(err?.response?.data?.message || err?.message || 'Failed to send code');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!code || code.length < 6) {
            setError('Please enter the 6-digit code.');
            return;
        }

        setLoading(true);
        try {
            await dispatch(verifyTwoFactor(email, code));

            // Clear 2FA related data
            localStorage.removeItem('pending_2fa');
            localStorage.removeItem('pending_2fa_email');
            localStorage.removeItem('pending_2fa_purpose');

            // For login: Automatically retry login with stored credentials
            if (purpose === 'login' && credentials) {
                const { username, password } = credentials;
                localStorage.removeItem('pending_2fa_credentials');
                
                // Retry login - this will now succeed since 2FA is verified
                await dispatch(userLoginAndAuthentication(username, password));
                // Back to the app that sent the user here (Octopus), if any
                leaveForReturnTo();
                return;
            }

            // For registration, just navigate to login
            navigate('/login');
        } catch (err) {
            // Handle 400 - Invalid code
            if (err?.response?.status === 400) {
                setError('Invalid code, please try again');
            } else {
                setError(err?.response?.data?.message || err?.message || 'Verification failed');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen-container">
            <div className="d-flex justify-content-center align-items-center mt-4">
                <div className="d-flex justify-content-center align-items-center w-100">
                    <div className="login-form">
                        <form className="login-form" onSubmit={handleVerify}>
                            <h2 className="my-4" style={{ userSelect: 'none' }}>Two-Factor Authentication</h2>
                            <p className="text-center">
                                {codeSent 
                                    ? `Verification code sent to ${email}`
                                    : 'Sending verification code...'}
                            </p>
                            {codeSent && (
                                <p className="text-center text-warning" style={{ fontSize: '0.9rem', marginTop: '-10px' }}>
                                    ⏱️ Code expires in 1 minute
                                </p>
                            )}

                            {codeSent ? (
                                <>
                                    <div className="form-data mt-4">
                                        <div className="form-group">
                                            <input
                                                className={`input-Box ${error ? 'input-error' : ''}`}
                                                type="text"
                                                name="code"
                                                value={code}
                                                onChange={(e) => setCode(e.target.value)}
                                                placeholder="Enter 6-digit code"
                                                maxLength={6}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {success && <p className="text-success mt-2 text-center">{success}</p>}
                                    {error && <p className="error-message text-danger mt-2">{error}</p>}

                                    <button type="submit" className="login-button" disabled={loading}>
                                        {loading ? 'Verifying...' : 'Verify'}
                                    </button>

                                    <div className="mt-3 text-center">
                                        <button
                                            type="button"
                                            className="btn btn-link"
                                            onClick={handleSendCode}
                                            disabled={resendCooldown > 0 || loading}
                                        >
                                            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="mt-3 text-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    {error && <p className="error-message text-danger mt-3">{error}</p>}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Verify2FA;
